/** @file lerpNodeKey: the continuous fields of two node keys interpolated (rot by shortest arc, pos, scale). */
import type { NodeKey } from '@zilo/pose3d-format/types/clip';
import { lerpAngleDeg } from '@zilo/pose3d-math/angle/lerpAngleDeg';

type Vec = [number, number, number];
const ZERO: Vec = [0, 0, 0];
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

function lerpVec(a: Vec | undefined, b: Vec | undefined, t: number, f: (x: number, y: number, t: number) => number): Vec | undefined {
  if (a === undefined && b === undefined) return undefined;
  const [p, q] = [a ?? ZERO, b ?? ZERO];
  return [f(p[0], q[0], t), f(p[1], q[1], t), f(p[2], q[2], t)];
}

/** `rot` per component with `lerpAngleDeg`, `pos` and `scale` linearly; a field on one side only starts from identity. */
export function lerpNodeKey(a: NodeKey, b: NodeKey, t: number): Pick<NodeKey, 'rot' | 'pos' | 'scale'> {
  const rot = lerpVec(a.rot, b.rot, t, lerpAngleDeg);
  const pos = lerpVec(a.pos, b.pos, t, lerp);
  const scale = a.scale === undefined && b.scale === undefined ? undefined : lerp(a.scale ?? 1, b.scale ?? 1, t);
  return { ...(rot === undefined ? {} : { rot }), ...(pos === undefined ? {} : { pos }), ...(scale === undefined ? {} : { scale }) };
}
